import * as React from 'react';

import type { SiteRedirect } from '@gitbook/api-client';
import * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    type ButtonProps,
    DataTable,
    type DataTableColumns,
    DataTableFooter,
    DataTableHeader,
    DataTableLayout,
    DataTableRoot,
    Description,
    DialogRoot,
    DialogTrigger,
    EmptyState,
    EmptyStateActions,
    EmptyStateIllustration,
    Heading,
    Image,
    type OverlayTriggerState,
    Pagination,
    Panel,
    SearchInput,
    useDialogTriggerState,
} from '@gitbook/spine-react';
import { useDebouncedState } from '@gitbook/util-react-hooks';

import { DocumentationLink, PaidFeatureIndicator } from '../../../../../Components';
import {
    apiResource,
    type UseUpgradeSite,
    useSiteContext,
    useSuspensePaginatedAPIQuery,
    useUpgradeSite,
} from '../../../../../state';
import { SiteSettingsHeader } from '../..';
import { AddRedirectDialog } from './AddRedirectDialog';
import { EditRedirectDialog } from './EditRedirectDialog';
import redirectIllustrationSrc from './redirect-illustration.svg';
import {
    SiteRedirectDestinationLabel,
    SiteRedirectDestinationLabelSkeleton,
} from './SiteRedirectDestinationLabel';

const SITE_REDIRECTS_DOCS_PATH = 'published-documentation/site-redirects';

export function SiteRedirectsPanel() {
    // Detach the state of the add dialog because if triggered from the empty state, it will be
    // unmounted when the empty state is unmounted, causing the dialog to be unmounted as well.
    const addDialogTrigger = useDialogTriggerState();
    // Detach the state of the edit dialog because it will be unmounted when we delete
    // from the redirect dialog.
    const editDialogTrigger = useDialogTriggerState();
    // Keep track of the edited redirect, since we need to pass it to the edit dialog.
    const [editedRedirect, setEditedRedirect] = React.useState<SiteRedirect | null>(null);

    const { site, organizationId } = useSiteContext();
    const upgrade = useUpgradeSite(site, api.SiteFeatureKey.SitesRedirects);

    const [searchQuery, debouncedSearchQuery, setSearchQuery] = useDebouncedState('');
    const { data, range, next, previous } = useSuspensePaginatedAPIQuery(
        apiResource('listSiteRedirects', {
            organizationId,
            siteId: site.id,
            limit: 50,
            search: debouncedSearchQuery,
        })
    );

    const columns: DataTableColumns<SiteRedirect> = React.useMemo(
        () => [
            {
                key: 'source',
                label: 'Source',
                isRowHeader: true,
                cell: (row) => row.item.source,
            },
            {
                key: 'arrow',
                label: '',
                width: 16, // Size of the icon
                cell: () => <Icon.ArrowRight className="text-muted" size={16} />,
            },
            {
                key: 'destination',
                label: 'Destination',
                cell: (row) => (
                    <React.Suspense fallback={<SiteRedirectDestinationLabelSkeleton />}>
                        <SiteRedirectDestinationLabel destination={row.item.destination} />
                    </React.Suspense>
                ),
            },
            {
                key: 'edit',
                label: '',
                width: 32, // Size of the button
                cell: (row) => (
                    <DialogTrigger trigger={editDialogTrigger}>
                        <Button
                            iconOnly
                            label="Edit"
                            kind="blank"
                            leadingIcon={Icon.Edit}
                            onPress={() => {
                                if (upgrade) {
                                    return upgrade.onPress();
                                }
                                setEditedRedirect(row.item);
                            }}
                        />
                    </DialogTrigger>
                ),
            },
        ],
        [editDialogTrigger, upgrade]
    );

    return (
        <>
            <SiteSettingsHeader
                headingId="redirects"
                heading={
                    <>
                        Redirects
                        <PaidFeatureIndicator
                            upgrade={upgrade}
                            isFeatureActive={data.items.length > 0}
                        />
                    </>
                }
                description="Set up site redirects to route traffic to content anywhere on your site."
                actions={
                    data.items.length ? (
                        <AddRedirectButton upgrade={upgrade} trigger={addDialogTrigger} />
                    ) : null
                }
            />

            <Panel>
                {data.items.length === 0 && !debouncedSearchQuery ? (
                    <div className="rounded-lg border bg-base">
                        <SiteRedirectsEmptyState
                            upgrade={upgrade}
                            addDialogTrigger={addDialogTrigger}
                        />
                    </div>
                ) : (
                    <>
                        <DataTableRoot
                            onSearchQueryChange={setSearchQuery}
                            searchQuery={searchQuery}
                        >
                            <DataTableLayout>
                                <DataTableHeader>
                                    <SearchInput placeholder="Find source..." value={searchQuery} />
                                </DataTableHeader>
                                <DataTable
                                    aria-label="Redirects"
                                    columns={columns}
                                    items={data.items}
                                    emptyState={
                                        <EmptyState>
                                            <Heading>
                                                No redirects with a source containing "
                                                {debouncedSearchQuery}"
                                            </Heading>
                                            <EmptyStateActions>
                                                <Button
                                                    label="Clear search"
                                                    kind="secondary"
                                                    leadingIcon={Icon.X}
                                                    loadable={[
                                                        searchQuery !== debouncedSearchQuery,
                                                    ]}
                                                    onPress={() => setSearchQuery('')}
                                                />
                                            </EmptyStateActions>
                                        </EmptyState>
                                    }
                                />
                                <DataTableFooter>
                                    <Pagination range={range} next={next} previous={previous} />
                                </DataTableFooter>
                            </DataTableLayout>
                        </DataTableRoot>
                        <Description>
                            GitBook automatically creates redirects when pages are moved.{' '}
                            <DocumentationLink path={SITE_REDIRECTS_DOCS_PATH}>
                                Learn more about redirects
                            </DocumentationLink>
                            .
                        </Description>
                    </>
                )}
                <DialogRoot {...addDialogTrigger.rootProps}>
                    <AddRedirectDialog />
                </DialogRoot>
                {editedRedirect && (
                    <DialogRoot {...editDialogTrigger.rootProps}>
                        <EditRedirectDialog siteRedirect={editedRedirect} />
                    </DialogRoot>
                )}
            </Panel>
        </>
    );
}

function SiteRedirectsEmptyState({
    upgrade,
    addDialogTrigger,
}: {
    upgrade: UseUpgradeSite | null;
    addDialogTrigger: OverlayTriggerState;
}) {
    return (
        <EmptyState>
            <EmptyStateIllustration>
                <Image src={redirectIllustrationSrc} alt="" width={65} height={64} />
            </EmptyStateIllustration>
            <Heading>No redirects</Heading>
            <Description>
                Set up site redirects to route traffic to content anywhere on your site.
                <br />
                Learn more about redirects in{' '}
                <DocumentationLink path={SITE_REDIRECTS_DOCS_PATH}>
                    our documentation
                </DocumentationLink>
                .
            </Description>
            <EmptyStateActions>
                <AddRedirectButton kind="upgrade" upgrade={upgrade} trigger={addDialogTrigger} />
            </EmptyStateActions>
        </EmptyState>
    );
}

function AddRedirectButton({
    upgrade,
    kind,
    trigger,
}: {
    upgrade: UseUpgradeSite | null;
    kind?: 'upgrade';
    trigger: OverlayTriggerState;
}) {
    if (upgrade) {
        return (
            <AddRedirectBaseButton
                {...(kind === 'upgrade' ? upgrade : { onPress: upgrade.onPress })}
            />
        );
    }
    return (
        <DialogTrigger trigger={trigger}>
            <AddRedirectBaseButton />
        </DialogTrigger>
    );
}

const AddRedirectBaseButton = React.forwardRef(function AddRedirectBaseButton(
    props: Partial<ButtonProps>,
    ref: React.ForwardedRef<HTMLButtonElement>
) {
    return (
        <Button
            ref={ref}
            kind="secondary"
            leadingIcon={Icon.Plus}
            label="Add redirect"
            {...props}
        />
    );
});
